import { OriginalData } from "../../Models/OriginalData";
import { OriginalHeadCount } from "../../Models/OriginalHeadCount";
import useDataverseService from "../../services/DataverseService";
import { useDataChanged } from "../Contexts/DataChangedProvider";
import { useData } from "../Contexts/DataProvider";
import { useRibbon } from "../Contexts/RibbonProvider";
import { useSelectedEntity } from "../Contexts/SelectedEntityProvider";
import { useSelectedTab } from "../Contexts/SelectedTabProvider";

export const useSave = () => {
  const { selectedTab } = useSelectedTab();
  const { setDataChanged } = useDataChanged();
  const { data, originalData } = useData();
  const dataverseService = useDataverseService();
  const { selectedEntity } = useSelectedEntity();
  const { addRibbon } = useRibbon();

  function chunkArray<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  }

  function getUpdates<T extends { [id: string]: any }>(
    dataArr: T[],
    originalArr: T[],
    idKey: string,
    entitySetName: string
  ) {
    return dataArr.reduce<any[]>((acc, row) => {
      const originalRow = originalArr.find((r) => r[idKey] === row[idKey]);
      if (!originalRow) return acc;

      const properties: { [k: string]: any } = {};
      Object.keys(row).forEach((key) => {
        if (
          key !== idKey &&
          row[key] !== originalRow[key] &&
          row[key] !== undefined
        ) {
          properties[key] = row[key];
        }
      });

      if (Object.keys(properties).length > 0) {
        acc.push({
          rowId: row[idKey],
          properties,
          entitySetName,
        });
      }
      return acc;
    }, []);
  }

  const handleSaveChanges = async (
    updatedData?: {
      value: OriginalData[];
      headCount: OriginalHeadCount[];
    },
    updatedOriginalData?: {
      value: OriginalData[];
      headCount: OriginalHeadCount[];
    }
  ) => {
    const workingData = updatedData ?? data;
    const workingOriginalData = updatedOriginalData ?? originalData;

    if (!workingData || !workingOriginalData) return;

    let updates: any[] = [];

    if (selectedTab === "Budget") {
      if (!workingData.value || !workingOriginalData.value) return;
      updates = getUpdates(
        workingData.value,
        workingOriginalData.value,
        "cr864_budgetrecordid",
        "cr864_budgetrecords"
      );
    } else if (selectedTab === "FTE") {
      if (!workingData.headCount || !workingOriginalData.headCount) return;
      updates = getUpdates(
        workingData.headCount,
        workingOriginalData.headCount,
        "new_headcountid",
        "new_headcounts"
      );
    }

    const filteredUpdates = updates.filter(
      (update) => Object.keys(update.properties).length > 0
    );

    if (filteredUpdates.length === 0) {
      addRibbon("No changes to save.", "info");
      return;
    }

    const chunks = chunkArray(filteredUpdates, 200);

    try {
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];

        const batchRequests = chunk.map((update, idx) => ({
          method: "PATCH",
          url: `${process.env["REACT_APP_DATAVERSE_URL"]}api/data/v9.1/${update.entitySetName}(${update.rowId})`,
          body: update.properties,
          contentId: `${idx + 1}`,
        }));

        await dataverseService.putDataBatch(batchRequests);
      }

      setDataChanged(false);
      addRibbon("Your data has been saved successfully.", "info");
    } catch (error) {
      addRibbon("Error saving data. Please try again.", "error");
      console.error("Error saving data:", error);
    }
  };

  return { handleSaveChanges };
};
